import type { ComponentType } from "react";
import { isModuleAbsent } from "./lib/module-absent";
import type { FlowErrorProps } from "./types";

export type { FlowErrorProps };

// Memoized per tenant so the promise identity is stable across renders —
// the flux route's `error.tsx` hands it to React's `use()`, which suspends on
// the first call and reads the settled value on every re-render after that.
const errorPageCache = new Map<
  string,
  Promise<ComponentType<FlowErrorProps> | null>
>();

/**
 * A tenant's optional `error.tsx` (`./<tenant>/error.tsx`), rendered by the
 * flux route's `error.tsx` in place of its default page. Resolves to `null`
 * when the tenant doesn't supply one.
 *
 * Lives in its own entry (`@tillix/flux/client`) rather than beside
 * `getTenantComponents`: Next's `error.tsx` is a client component, and the
 * package index reaches the flow registry (server-only code) that can't be
 * pulled into a client bundle.
 */
export function getTenantErrorPage(
  tenantId: string,
): Promise<ComponentType<FlowErrorProps> | null> {
  let pending = errorPageCache.get(tenantId);
  if (!pending) {
    pending = import(`./${tenantId}/error`)
      .then((mod: { default: ComponentType<FlowErrorProps> }) => mod.default)
      .catch((err: unknown) => {
        if (!isModuleAbsent(err)) {
          console.error(
            "[flux] tenant error page failed to load; using the default",
            tenantId,
            err,
          );
        }
        return null;
      });
    errorPageCache.set(tenantId, pending);
  }
  return pending;
}
